import React, { useEffect } from 'react';
import { Coins, PiggyBank, TrendingUp, TrendingDown, AlertCircle, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCreditStore } from '../../store/creditStore';
import { useAuthStore } from '../../store/authStore';

interface CreditDisplayProps {
  variant?: 'compact' | 'full';
  showRefreshButton?: boolean;
}

const CreditDisplay: React.FC<CreditDisplayProps> = ({ 
  variant = 'compact',
  showRefreshButton = false
}) => {
  const { credits, isLoading, error, fetchCredits } = useCreditStore();
  const { user } = useAuthStore();
  
  useEffect(() => {
    if (user?.id) {
      fetchCredits(user.id);
    }
  }, [user?.id, fetchCredits]);
  
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-1 text-sm">
        <Coins size={16} className="text-yellow-500" />
        <span className={`font-semibold ${credits < 100 ? 'text-red-400' : ''}`}>
          {isLoading ? '...' : credits}
        </span>
      </div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-secondary-dark rounded-lg p-4 border border-gray-800"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <PiggyBank className="h-5 w-5 text-yellow-500 mr-2" />
          <h3 className="text-lg font-semibold">Credit Balance</h3>
        </div>
        
        {showRefreshButton && user?.id && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fetchCredits(user.id)}
            disabled={isLoading}
            className="p-1 rounded hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            <TrendingUp size={16} />
          </motion.button>
        )}
      </div>
      
      {error ? (
        <div className="flex items-center text-red-400 text-sm">
          <AlertCircle size={16} className="mr-1" />
          <span>{error}</span>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="text-3xl font-bold">
            {isLoading ? 
              <span className="text-gray-400">Loading...</span> : 
              <span className={credits < 100 ? 'text-red-400' : 'text-yellow-400'}>
                {credits}
              </span>
            }
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center px-3 py-1.5 rounded bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm"
          >
            <CreditCard size={14} className="mr-1" />
            <span>Buy Credits</span>
          </motion.button>
        </div>
      )}
      
      {credits < 100 && (
        <div className="mt-2 text-xs text-red-400 flex items-center">
          <AlertCircle size={12} className="mr-1" />
          <span>Low credit balance. Some features may be unavailable.</span>
        </div>
      )}
    </motion.div>
  );
};

export default CreditDisplay;